import { z } from 'zod';
import { MoneySchema } from './common.js';
import { LvaConditionSchema } from './lva.js';
// Prisma Decimal columns serialize as canonical strings; allow either string or
// non-negative number on the wire (matches the convention in lva.ts / ar.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
// Per-line count outcome. PENDING = not yet counted; the four variances drive
// the close-time resolution (MISSING→LOST, MOVED→relocate, DAMAGED→condition);
// NOT_IN_REGISTER = a scanned code not in the round snapshot (an extra line).
export const LvaCountStatusSchema = z.enum([
    'PENDING',
    'FOUND',
    'MISSING',
    'MOVED',
    'DAMAGED',
    'NOT_IN_REGISTER',
]);
// Round accounting status (the Design-Z dual-status sibling of the engine
// approvalStatus): COUNTING while lines are classified, PENDING_APPROVAL once
// submitted to the engine, CLOSED on final approval (REJECTED reverts to COUNTING).
export const LvaCountRoundStatusSchema = z.enum(['COUNTING', 'PENDING_APPROVAL', 'CLOSED']);
// ============================================================
// LvaCountRound — the round + engine doc (dual-status)
// roundNo = PCR-YYYY-NNNNN. scope* null = no filter (all in-scope IN_USE items).
// engine cols (approvalStatus/currentStep/...) mirror the AP-bill/CN Design-Z
// pattern: the accounting `status` is decoupled from the engine `approvalStatus`.
// ============================================================
export const LvaCountRoundSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    roundNo: z.string().min(1),
    period: z.string().min(1),
    plannedDate: z.coerce.date(),
    dueDate: z.coerce.date().nullable().optional(),
    scopeBranchId: z.uuid().nullable().optional(),
    scopeDepartmentId: z.uuid().nullable().optional(),
    scopeCategoryId: z.uuid().nullable().optional(),
    status: LvaCountRoundStatusSchema,
    // Engine columns (the central approval engine writes these).
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.number().int().nullable().optional(),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    openedAt: z.coerce.date(),
    openedBy: z.string().min(1),
    itemCount: z.number().int().nonnegative(),
    closedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — adds resolved scope display names for table rendering.
export const LvaCountRoundListRowSchema = LvaCountRoundSchema.extend({
    scopeBranchName: z.string().nullable().optional(),
    scopeDepartmentName: z.string().nullable().optional(),
    scopeCategoryName: z.string().nullable().optional(),
});
// ============================================================
// LvaCountLine — one per snapshotted in-scope item + extras.
// lvaItemId is null for NOT_IN_REGISTER extras (snapCode/snapName free text).
// snap* fields freeze the expected state at round-open (immutable).
// ============================================================
export const LvaCountLineSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    roundId: z.uuid(),
    lvaItemId: z.uuid().nullable().optional(),
    // Snapshot (frozen at round-open).
    snapCode: z.string().min(1),
    snapName: z.string().min(1),
    snapBranchId: z.uuid().nullable().optional(),
    snapDepartmentId: z.uuid().nullable().optional(),
    snapLocation: z.string().nullable().optional(),
    snapCustodianEmployeeId: z.uuid().nullable().optional(),
    snapCondition: LvaConditionSchema.nullable().optional(),
    snapCost: DecimalLike.nullable().optional(),
    // Result.
    countStatus: LvaCountStatusSchema.default('PENDING'),
    scannedAt: z.coerce.date().nullable().optional(),
    scannedBy: z.string().nullable().optional(),
    foundBranchId: z.uuid().nullable().optional(),
    foundLocation: z.string().nullable().optional(),
    conditionFound: LvaConditionSchema.nullable().optional(),
    note: z.string().nullable().optional(),
    resolved: z.boolean().default(false),
    resolutionMovementCode: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — adds resolved display names for the count grid.
export const LvaCountLineListRowSchema = LvaCountLineSchema.extend({
    snapBranchName: z.string().nullable().optional(),
    snapDepartmentName: z.string().nullable().optional(),
    snapCustodianName: z.string().nullable().optional(),
    foundBranchName: z.string().nullable().optional(),
});
// ============================================================
// CreateLvaCountRound — POST body
// roundNo is server-generated (PCR-YYYY-NNNNN). scope* omitted = all in-scope.
// ============================================================
export const CreateLvaCountRoundSchema = z.object({
    period: z.string().min(1),
    plannedDate: z.iso.date(),
    dueDate: z.iso.date().optional(),
    scopeBranchId: z.uuid().optional(),
    scopeDepartmentId: z.uuid().optional(),
    scopeCategoryId: z.uuid().optional(),
});
// ============================================================
// ClassifyLvaCountLine — PATCH :id/lines/:lineId body
// Manually sets the per-line outcome. Refines: MOVED needs foundBranchId or
// foundLocation (something must have changed); DAMAGED needs conditionFound.
// ============================================================
export const ClassifyLvaCountLineSchema = z
    .object({
    countStatus: LvaCountStatusSchema,
    foundBranchId: z.uuid().nullable().optional(),
    foundLocation: z.string().min(1).nullable().optional(),
    conditionFound: LvaConditionSchema.nullable().optional(),
    note: z.string().min(1).nullable().optional(),
})
    .refine((v) => v.countStatus !== 'MOVED' || v.foundBranchId != null || v.foundLocation != null, {
    message: 'A MOVED line must record a found branch or a found location',
    path: ['foundLocation'],
})
    .refine((v) => v.countStatus !== 'DAMAGED' || v.conditionFound != null, {
    message: 'A DAMAGED line must record the found condition',
    path: ['conditionFound'],
});
// ============================================================
// AddCountExtra — POST :id/extras body
// Records a scanned/found code that was not in the round snapshot as a
// NOT_IN_REGISTER extra line (lvaItemId null; free-text code/name).
// ============================================================
export const AddCountExtraSchema = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
    foundLocation: z.string().min(1).optional(),
    note: z.string().min(1).optional(),
});
// ============================================================
// CountScan — POST :id/scan body
// A single scanned/entered asset code; the server resolves it against the round
// snapshot (FOUND, or a "not in this round" signal so the UI offers add-extra).
// ============================================================
export const CountScanSchema = z.object({
    code: z.string().min(1),
});
//# sourceMappingURL=lva-count.js.map